import React, { useRef } from 'react';
import {
  MousePointer2,
  Minus,
  Square,
  DoorOpen,
  Ruler,
  Undo2,
  Redo2,
  ImagePlus,
  Trash2,
  GitMerge,
  Scissors,
} from 'lucide-react';
import { useProjectStore } from '../../../store/projectStore';
import { CanvasMode } from '../../../types';
import { wallCanSplit } from '../../../utils/floorPlanGeometry';

const modes: Array<{
  id: CanvasMode;
  label: string;
  shortcut: string;
  icon: React.ReactNode;
  colorClass: string;
  activeBg: string;
}> = [
  {
    id: 'select',
    label: 'Select',
    shortcut: 'V',
    icon: <MousePointer2 size={16} />,
    colorClass: 'text-slate-300',
    activeBg: 'bg-slate-500 hover:bg-slate-600 text-white',
  },
  {
    id: 'wall',
    label: 'Wall',
    shortcut: 'W',
    icon: <Minus size={16} />,
    colorClass: 'text-amber-400',
    activeBg: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  {
    id: 'room',
    label: 'Room',
    shortcut: 'R',
    icon: <Square size={16} />,
    colorClass: 'text-sky-300',
    activeBg: 'bg-sky-600 hover:bg-sky-700 text-white',
  },
  {
    id: 'door',
    label: 'Door',
    shortcut: 'D · R flip',
    icon: <DoorOpen size={16} />,
    colorClass: 'text-rose-400',
    activeBg: 'bg-rose-600 hover:bg-rose-700 text-white',
  },
  {
    id: 'measure',
    label: 'Measure',
    shortcut: 'M',
    icon: <Ruler size={16} />,
    colorClass: 'text-teal-400',
    activeBg: 'bg-teal-600 hover:bg-teal-700 text-white',
  },
];

/** Icon-only floor-plan tools — same visual language as the designer toolbar buttons */
const FloorPlanToolbar: React.FC = () => {
  const fileRef = useRef<HTMLInputElement>(null);
  const {
    canvasMode,
    setCanvasMode,
    getFloorPlan,
    setFloorPlanUnits,
    selectedFloorPlanWallId,
    selectedFloorPlanWallIds,
    selectedFloorPlanRoomId,
    selectedFloorPlanOpeningId,
    selectedReferenceImageId,
    deleteWall,
    deleteWalls,
    deleteRoom,
    deleteOpening,
    removeFloorPlanReferenceImage,
    mergeWalls,
    splitWall,
    undoFloorPlan,
    redoFloorPlan,
    floorPlanHistoryPast,
    floorPlanHistoryFuture,
    queueFloorPlanReferenceImages,
  } = useProjectStore();

  const floorPlan = getFloorPlan();
  const units = floorPlan?.units ?? 'metric';

  const handleDelete = () => {
    if (selectedReferenceImageId) removeFloorPlanReferenceImage(selectedReferenceImageId);
    else if (selectedFloorPlanOpeningId) deleteOpening(selectedFloorPlanOpeningId);
    else if (selectedFloorPlanRoomId) deleteRoom(selectedFloorPlanRoomId);
    else if (selectedFloorPlanWallIds.length > 0) deleteWalls(selectedFloorPlanWallIds);
    else if (selectedFloorPlanWallId) deleteWall(selectedFloorPlanWallId);
  };

  const canMergeWalls = (selectedFloorPlanWallIds ?? []).length === 2;
  const handleMerge = () => {
    if (!canMergeWalls) return;
    const [idA, idB] = selectedFloorPlanWallIds;
    const result = mergeWalls(idA, idB);
    if (!result.ok) {
      window.alert(result.reason);
    }
  };

  const splitTargetId =
    (selectedFloorPlanWallIds ?? []).length === 1
      ? selectedFloorPlanWallIds[0]
      : (selectedFloorPlanWallIds ?? []).length === 0
        ? selectedFloorPlanWallId
        : null;
  const canSplitWall = !!(
    splitTargetId &&
    floorPlan &&
    wallCanSplit(floorPlan.walls, splitTargetId)
  );
  const handleSplit = () => {
    if (!splitTargetId) return;
    const result = splitWall(splitTargetId);
    if (!result.ok) {
      window.alert(result.reason);
    }
  };

  const hasSelection =
    !!selectedReferenceImageId ||
    (selectedFloorPlanWallIds ?? []).length > 0 ||
    selectedFloorPlanWallId ||
    selectedFloorPlanRoomId ||
    selectedFloorPlanOpeningId;

  const handleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const loadFile = (file: File) =>
      new Promise<{
        url: string;
        name: string;
        naturalWidth: number;
        naturalHeight: number;
        x: number;
        y: number;
        width: number;
        height: number;
        opacity: number;
      } | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const url = String(reader.result || '');
          if (!url) {
            resolve(null);
            return;
          }
          const img = new Image();
          img.onload = () => {
            resolve({
              url,
              name: file.name,
              naturalWidth: img.width,
              naturalHeight: img.height,
              x: 0,
              y: 0,
              width: img.width,
              height: img.height,
              opacity: 0.45,
            });
          };
          img.onerror = () => resolve(null);
          img.src = url;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });

    void Promise.all(files.map(loadFile)).then((loaded) => {
      const images = loaded.filter((x): x is NonNullable<typeof x> => !!x);
      if (images.length > 0) {
        queueFloorPlanReferenceImages(
          images.map(({ url, name, naturalWidth, naturalHeight, width, height, opacity }) => ({
            url,
            name,
            naturalWidth,
            naturalHeight,
            width,
            height,
            opacity,
          }))
        );
      }
    });
    e.target.value = '';
  };

  const btnBase =
    'px-2.5 transition-colors flex items-center justify-center h-8';

  return (
    <>
      {/* Divider between designer tools and floor-plan tools */}
      <div className="w-px h-5 bg-slate-600/80 self-center mx-0.5 flex-shrink-0" />

      {modes.map((m) => {
        const active = canvasMode === m.id;
        return (
          <div key={m.id} className="relative group">
            <button
              type="button"
              title={`${m.label} (${m.shortcut})`}
              onClick={() => setCanvasMode(active ? 'place' : m.id)}
              className={`${btnBase} ${
                active
                  ? m.activeBg
                  : 'bg-slate-700 hover:bg-slate-600'
              }`}
            >
              <span className={active ? 'text-white' : m.colorClass}>{m.icon}</span>
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
              <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                {m.label}
                <span className="ml-1.5 text-slate-400">{m.shortcut}</span>
                <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
              </div>
            </div>
          </div>
        );
      })}

      <div className="w-px h-5 bg-slate-600/80 self-center mx-0.5 flex-shrink-0" />

      <div className="relative group">
        <button
          type="button"
          title={`Units: ${units === 'metric' ? 'meters' : 'feet'} (click to toggle)`}
          onClick={() => setFloorPlanUnits(units === 'metric' ? 'imperial' : 'metric')}
          className={`${btnBase} ${
            // Always show current unit as the “active” chip style
            'bg-slate-700 hover:bg-slate-600'
          } min-w-[2rem]`}
        >
          <span className="text-[11px] font-semibold text-orange-300 uppercase leading-none">
            {units === 'metric' ? 'm' : 'ft'}
          </span>
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            Lengths in {units === 'metric' ? 'meters' : 'feet'} (type e.g. 5 or 0.5)
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>

      <div className="w-px h-5 bg-slate-600/80 self-center mx-0.5 flex-shrink-0" />

      <div className="relative group">
        <button
          type="button"
          title="Undo (Ctrl+Z)"
          onClick={() => undoFloorPlan()}
          disabled={floorPlanHistoryPast.length === 0}
          className={`${btnBase} bg-slate-700 hover:bg-slate-600 disabled:opacity-30`}
        >
          <Undo2 size={16} className="text-slate-300" />
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            Undo
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>

      <div className="relative group">
        <button
          type="button"
          title="Redo (Ctrl+Y)"
          onClick={() => redoFloorPlan()}
          disabled={floorPlanHistoryFuture.length === 0}
          className={`${btnBase} bg-slate-700 hover:bg-slate-600 disabled:opacity-30`}
        >
          <Redo2 size={16} className="text-slate-300" />
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            Redo
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>

      <div className="relative group">
        <button
          type="button"
          title="Add reference image(s) — then click canvas to place"
          onClick={() => fileRef.current?.click()}
          className={`${btnBase} bg-slate-700 hover:bg-slate-600`}
        >
          <ImagePlus size={16} className="text-violet-300" />
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            Add reference image(s) — click canvas to place
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleReferenceUpload}
      />

      <div className="relative group">
        <button
          type="button"
          title="Merge walls (select two with Shift)"
          onClick={handleMerge}
          disabled={!canMergeWalls}
          className={`${btnBase} bg-slate-700 hover:bg-slate-600 disabled:opacity-30`}
        >
          <GitMerge size={16} className="text-amber-300" />
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            {canMergeWalls ? 'Merge walls' : 'Merge walls — Shift-click two walls'}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>

      <div className="relative group">
        <button
          type="button"
          title="Split wall at junctions"
          onClick={handleSplit}
          disabled={!canSplitWall}
          className={`${btnBase} bg-slate-700 hover:bg-slate-600 disabled:opacity-30 ${
            hasSelection ? '' : 'rounded-r-lg'
          }`}
        >
          <Scissors size={16} className="text-sky-300" />
        </button>
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
          <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
            {canSplitWall
              ? 'Split wall at junction'
              : 'Split wall — select a wall that another wall meets in the middle'}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
          </div>
        </div>
      </div>

      {hasSelection && (
        <div className="relative group">
          <button
            type="button"
            title="Delete selected (Del)"
            onClick={handleDelete}
            className={`${btnBase} bg-slate-700 hover:bg-red-900/60 rounded-r-lg`}
          >
            <Trash2 size={16} className="text-red-400" />
          </button>
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none z-50">
            <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
              Delete
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloorPlanToolbar;
